import { useFormContext } from '@calendly/form';
import { I18n } from '@calendly/i18n';
import classNames from 'classnames';
import { without } from 'lodash-es';
import { useEffect, useMemo, forwardRef } from 'react';
import { useETEditorFormikPoolingType } from 'services/event_types';
import { updateIntegrations, updateLocations } from './actions';
import { AddLocation } from './add_location';
import { DependentNotice } from './dependent_notice';
import { FormFieldLabel } from './form_field_label';
import { Label } from './label';
import { LocationList } from './location_list';
import { LocationPopup, PotentialKindPopup } from './location_popup';
import { initialState, reducer } from './reducer';
import { StateProvider, useDispatchValue } from './state_provider';
const POSSIBLE_KINDS = ['physical', 'inbound_call', 'outbound_call', 'google_conference', 'zoom_conference', 'microsoft_teams_conference', 'webex_conference', 'gotomeeting_conference', 'custom', 'ask_invitee'];
const NEW_POSSIBLE_KINDS = ['inbound_call', 'outbound_call', 'physical', 'ask_invitee', 'custom', 'zoom_conference', 'google_conference', 'microsoft_teams_conference', 'webex_conference', 'gotomeeting_conference'];
const fieldsetCss = "fsjebov";
const Fieldset = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(fieldsetCss, props.className)} />;
});
const editableNoticeCss = "e12td2if";
const EditableNotice = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(editableNoticeCss, props.className)} />;
});
const extractIntegrationsStatus = (eventType, conferenceOrganizer) => {
  const calendarIntegrationStatusSourceObject = eventType.id && eventType.pooling_type === 'collective' ? eventType : conferenceOrganizer;
  return {
    zoom_conference: conferenceOrganizer.is_zoom_configured,
    gotomeeting_conference: conferenceOrganizer.is_gotomeeting_configured,
    google_conference: calendarIntegrationStatusSourceObject.google_conferencing_configured,
    microsoft_teams_conference: calendarIntegrationStatusSourceObject.is_microsoft_teams_configured,
    webex_conference: conferenceOrganizer.is_webex_configured
  };
};
const newEventTypeKinds = ['one_on_one', 'group', 'collective', 'round_robin'];
const IntegrationStatusSync = ({
  integrationsStatus
}) => {
  const dispatch = useDispatchValue();
  useEffect(() => {
    if (integrationsStatus) {
      dispatch(updateIntegrations(integrationsStatus));
    }
  }, [integrationsStatus, dispatch]);
  return null;
};
const LocationConfigurationsSync = ({
  locationConfigurations
}) => {
  const dispatch = useDispatchValue();
  useEffect(() => {
    if (locationConfigurations) {
      dispatch(updateLocations(locationConfigurations));
    }
  }, [locationConfigurations, dispatch]);
  return null;
};
const isCollectiveRoundRobinWithoutCollective = values => (values.user_pools || values.userPools) && !(values.user_pools || values.userPools).some(pool => !pool._destroy && (pool.pooling_type || pool.poolingType) === 'collective');
export const LocationField = ({
  locationConfigurations,
  onChange,
  conferencingOrganizer,
  isDisabled = false,
  allowMultiple = false,
  eventType,
  isManaged,
  potentialKind,
  useUpdatedStyles = false,
  isSelectedForAllHosts = false,
  hideLabel = false,
  withIntegrationErrors = true,
  setIsSelectedForAllHosts = () => {},
  useNewLocationSelect = false,
  syncLocations = false,
  dropdownWrapper,
  hideErrors,
  ...props
}) => {
  const {
    values
  } = useFormContext();
  const {
    isCollective,
    isRoundRobin,
    isCollectiveRoundRobin
  } = useETEditorFormikPoolingType();
  const isCRRWithoutCollectivePool = isCollectiveRoundRobin && isCollectiveRoundRobinWithoutCollective(values);
  const showLabel = !hideLabel && !isRoundRobin;
  const conferenceOrganizer = conferencingOrganizer || eventType;
  const useNewDropdown = useUpdatedStyles && newEventTypeKinds.includes(eventType.kind_name);
  let disabledTooltip;
  let possibleKinds = useNewDropdown ? NEW_POSSIBLE_KINDS : POSSIBLE_KINDS;
  let showIntegrationErrors = withIntegrationErrors;
  if (eventType.isGroup || eventType.isPoll) {
    possibleKinds = without(possibleKinds, 'ask_invitee', 'outbound_call');
    disabledTooltip = eventType.isGroup ? I18n.t('publisher.event_types.location.group_disabled_call_tooltip') : I18n.t('publisher.event_types.location.meeting_poll_disabled_call_tooltip');
    if (eventType.isTeam) showIntegrationErrors = false;
  }
  if (isRoundRobin || isCRRWithoutCollectivePool) {
    possibleKinds = without(possibleKinds, 'ask_invitee', 'outbound_call');
    disabledTooltip = I18n.t('publisher.event_types.location.round_robin_disabled_call_tooltip');
  }
  if (isCollective) {
    showIntegrationErrors = false;
  }
  if (isManaged) {
    showIntegrationErrors = false;
  }
  const integrationsStatus = useMemo(() => extractIntegrationsStatus(eventType, conferenceOrganizer), [eventType, conferenceOrganizer]);
  const label = useUpdatedStyles ? <FormFieldLabel /> : <Label />;
  return <StateProvider initialState={initialState({
    locations: locationConfigurations,
    potentialKind,
    possibleKinds,
    integrations: integrationsStatus,
    isPending: conferenceOrganizer.is_pending,
    disabledTooltip,
    isDisabled,
    showIntegrationErrors,
    showLocationVisibilityOptions: !isRoundRobin && !isCRRWithoutCollectivePool,
    kindName: eventType.kind_name,
    useNewDropdown,
    useNewLocationSelect,
    isNewEventType: !eventType.id
  })} reducer={reducer} onChange={onChange}>
      <IntegrationStatusSync integrationsStatus={integrationsStatus} />
      {syncLocations && <LocationConfigurationsSync locationConfigurations={locationConfigurations} />}
      <Fieldset {...props}>
        {potentialKind && <PotentialKindPopup />}
        {showLabel && label}
        {isManaged && eventType.id && <EditableNotice>
            {I18n.t('publisher.organization.managed_event_types.location_is_editable')}
          </EditableNotice>}
        <LocationList id="location-list" isSelectedForAllHosts={isSelectedForAllHosts} setIsSelectedForAllHosts={setIsSelectedForAllHosts} dropdownWrapper={dropdownWrapper} hideErrors={hideErrors} isDisabled={isDisabled} />
        <DependentNotice />
        {allowMultiple && <AddLocation />}
        <LocationPopup />
      </Fieldset>
    </StateProvider>;
};

require("./location_field.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./location_field.js");